import { useContext, useEffect, useMemo } from 'react';
import {
  PaginationContext,
  SortContext,
  InventoryTable,
  localStorage,
  InventorySectionContext,
} from 'dash-ui';
import {
  useGetUnitsQuery,
  useGetPropertyUnitsInPublicBrokerPortfolioQuery,
  PropertyFragment,
} from 'gql/generated.graphql';

import { PropertyPreviewParamsContext } from 'containers/properties/PropertyPreview/context/PropertyPreviewParamsContext';
import { isNil } from 'lodash';

function InventoryTableSection({
  property,
  isViewAsClient,
  featureAccess = false,
  isPortfolioView = false,
}: {
  isViewAsClient?: boolean;
  property: PropertyFragment;
  featureAccess?: boolean;
  isPortfolioView?: boolean;
}) {
  const { activeTab } = useContext(InventorySectionContext);
  const { params } = useContext(PropertyPreviewParamsContext);
  const { brokerId, propertyId, portfolioFolderId } = params || {};
  const {
    setTotal,
    state: paginationState,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { state: sort } = useContext(SortContext);

  const { skip } = getRefetchProps(paginationState);

  const portfolioVariables = {
    propertyId,
    brokerId,
    portfolioFolderId: portfolioFolderId || undefined,
    password: localStorage.getPortfolioPassword(),
  };

  const {
    data: result,
    loading,
  } = useGetUnitsQuery({
    skip: isViewAsClient,
    variables: {
      sort: sort.fieldName ? [sort] : undefined,
      pagination: { skip, limit: 5 },
      filter: { propertyId, inventoryType: activeTab },
    },
  });

  const {
    data: publicResult,
    loading: publicLoading,
  } = useGetPropertyUnitsInPublicBrokerPortfolioQuery({
    skip: isNil(portfolioVariables.brokerId) || isNil(isViewAsClient) || isNil(propertyId),
    variables: {
      ...portfolioVariables,
      sort: sort.fieldName ? [sort] : undefined,
      pagination: { skip, limit: 5 },
      filter: { propertyId, inventoryType: activeTab },
    },
  });

  const { data, total } = useMemo(
    () => ({
      data:
        result?.getUnits?.collection ||
        publicResult?.getPropertyUnitsInPublicBrokerPortfolio?.collection ||
        [],
    total:
      result?.getUnits?.totalCount ||
      publicResult?.getPropertyUnitsInPublicBrokerPortfolio?.totalCount ||
      0,
    }),
    [result, publicResult]
  );

  useEffect(() => {
    setTotal(total);
  }, [total]);

  return (
    <InventoryTable
      data={data}
      loading={loading || publicLoading}
      emptyStateText={property.defaultInventoryDetails || ''}
      featureAccess={featureAccess}
      isPortfolioView={isPortfolioView}
    />
  );
}

export default InventoryTableSection;
